<template>
    <div v-if="hasI18nLoaded" class="waste-type-size-finder">
        <div v-show="!selected" class="waste-type-size-finder__search">
            <label
                for="waste-type-size-finder-input"
                class="waste-type-size-finder__label"
                :class="{ 'waste-type-size-finder__label--disabled': isLoading }"
            >
                {{ $t('returnGuide.search.label') }}
            </label>
            <div
                class="waste-type-size-finder__input"
                :class="{ 'waste-type-size-finder__input--disabled': isLoading }"
            >
                <div class="autocomplete-wrapper">
                    <Autocomplete
                        id="waste-type-size-finder-input"
                        class="waste-type-size-finder__autocomplete"
                        :class="{ 'waste-type-size-finder__autocomplete--disabled': isLoading }"
                        :display-item="getResultValue"
                        :placeholder="$t('returnGuide.search.placeholder')"
                        :results="filteredProducts"
                        :max="100"
                        @input="search"
                        @onSelect="submit"
                    />
                    <div class="autocomplete__icon" />
                </div>
            </div>

            <div class="waste-type-size-finder__fieldset">
                <div class="waste-type-size-finder__label">
                    {{ $t('returnGuide.choice.label') }}
                </div>
            </div>

            <ul class="list waste-type-size-finder__sizes">
                <li v-for="wasteTypeChoice in wasteTypeChoices" :key="wasteTypeChoice.id" class="list">
                    <div class="waste-type-size">
                        <input
                            :id="'size-small-' + wasteTypeChoice.id"
                            :value="wasteTypeChoice.id"
                            :data-name="wasteTypeChoice.name"
                            class="waste-type-size__input"
                            v-model="selectedWasteType"
                            type="radio"
                            @change="selectSize"
                        />
                        <label class="waste-type-size__label" :for="'size-small-' + wasteTypeChoice.id">
                            {{ wasteTypeChoice.label }}
                            <div class="waste-type-size__description">
                                {{ wasteTypeChoice.description }}
                            </div>
                        </label>
                        <div class="waste-type-size__icon" aria-hidden="true"></div>
                    </div>
                </li>
            </ul>
        </div>
        <div v-show="selected" class="waste-type-size-finder__result">
            <h3>
                <template v-if="selected">
                    {{ $t('returnGuide.result.label', { device: selected.name }) }}
                </template>
                <template v-else>
                    {{ $t('returnGuide.result.labelDefault') }}
                </template>
            </h3>
            <p>
                <template v-if="selected && !selected.isGeneric">
                    {{ $t('returnGuide.result.description', { device: selected.name.toLowerCase() }) }}
                </template>
                <template v-else>
                    {{ $t('returnGuide.result.descriptionDefault') }}
                </template>
            </p>
            <br />
            <div v-html="$t('returnGuide.result.oldNew')" />
            <br />
            <p class="waste-type-size-finder__alert" v-if="selected && selected.returnCleanCategory">
                <strong> {{ $t('returnGuide.result.alertLabel') }} </strong><br />
                {{ $t('returnGuide.result.alertDescription', { category: selected.returnCleanCategory }) }}
            </p>
            <button class="button button--small button--secondary" type="button" @click="reset">
                {{ $t('returnGuide.actions.searchAgain') }}
            </button>
        </div>
    </div>
    <div v-else class="waste-type-size-finder-loader">
        <LoadingSpinner />
    </div>
</template>

<script>
    import Autocomplete from 'vue3-autocomplete'
    import 'vue3-autocomplete/dist/vue3-autocomplete.css'
    import LoadingSpinner from '../Loader/LoadingSpinner.vue'

    export default {
        name: 'ReturnGuide',
        props: ['pageId'],
        components: {
            Autocomplete,
            LoadingSpinner
        },
        data() {
            return {
                isLoading: false,
                selected: false,
                products: [],
                inputValue: '',
                wasteTypeChoices: [],
                selectedWasteType: null
            }
        },
        computed: {
            filteredProducts() {
                if (this.inputValue.length < 1) {
                    return []
                }

                return this.products.filter((product) => {
                    return product.name.toLowerCase().startsWith(this.inputValue.toLowerCase())
                })
            },
            hasI18nLoaded() {
                const messages = this.$i18n.messages[this.$i18n.locale]
                if (typeof messages === 'object' && Object.keys(messages).length > 0) {
                    return true
                }
                return false
            }
        },
        watch: {
            selected(newValue) {
                if (newValue) {
                    this.updateContentBlocks()
                } else {
                    this.showAllContentBlocks()
                }
            }
        },
        created() {
            this.fetchData()
        },
        methods: {
            fetchData() {
                this.isLoading = true
                const request = new Request('/dist/json/return-guide-collection.json?pageId=' + this.pageId, {
                    cache: 'no-cache'
                })
                fetch(request)
                    .then((response) => response.json())
                    .then((data) => {
                        this.products = data.products
                        this.wasteTypeChoices = data.wasteTypes
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            search(input) {
                this.inputValue = input
            },
            getResultValue(result) {
                return result.name
            },
            getCtaElements() {
                const ctaElements = document.querySelectorAll('[data-waste-types]')
                return ctaElements || []
            },
            hideAllContentBlocks() {
                const ctaElements = this.getCtaElements()
                ctaElements.forEach((ctaElement) => {
                    this.hideContentBlock(ctaElement)
                })
            },
            showAllContentBlocks() {
                const ctaElements = this.getCtaElements()
                ctaElements.forEach((ctaElement) => {
                    this.showContentBlock(ctaElement)
                })
            },
            updateContentBlocks() {
                this.hideAllContentBlocks()
                if (!this.selected) {
                    return
                }

                const ctaElements = this.getCtaElements()
                ctaElements.forEach((ctaElement) => {
                    const wastetypes = JSON.parse(ctaElement.getAttribute('data-waste-types'))
                    if (wastetypes.includes(this.selected.wasteType)) {
                        this.showContentBlock(ctaElement)
                    }
                })
            },
            showContentBlock(contentBlock) {
                if (!contentBlock) {
                    return
                }
                contentBlock.classList.remove('d-none')
            },
            hideContentBlock(contentBlock) {
                if (!contentBlock) {
                    return
                }
                contentBlock.classList.add('d-none')
            },
            selectSize(event) {
                const name = event.target.dataset.name
                this.selected = {
                    name,
                    wasteType: parseInt(event.target.value),
                    isGeneric: true
                }

                // TODO: TrackEvent() for GA
            },
            submit(submittedResult) {
                let result =
                    submittedResult || this.products.find((product) => product.name.toLowerCase() === this.inputValue)
                if (!result) {
                    this.selected = {
                        name: 'Apparaat',
                        wasteType: 0,
                        isGeneric: true
                    }
                } else {
                    this.selected = {
                        ...result,
                        isGeneric: false
                    }
                }

                // TODO: TrackEvent() for GA
            },
            async reset() {
                this.selected = null
                this.selectedWasteType = null

                // TODO: TrackEvent() for GA
            }
        }
    }
</script>

<style lang="scss">
    .waste-type-size-finder {
        &__input {
            &--disabled {
                cursor: not-allowed;
                opacity: 0.2;
            }
        }

        &__label,
        &__autocomplete {
            &--disabled {
                pointer-events: none;
            }
        }

        .waste-type-size-finder {
            &__autocomplete {
                border-color: $color-gray-dark;
                border-radius: 8px;
                border-width: 1px;
                margin: 0;
                padding: 15px 50px 15px 15px;
                width: 100%;
            }
        }

        .vue3-autocomplete-container {
            .vue3-results-container {
                border-color: $color-gray-dark;
                border-radius: 4px;
                max-height: 300px;
                overflow: auto;
                top: 100% !important;
            }

            .vue3-results-item {
                border-bottom-color: $color-gray-light !important;
                line-height: 40px;

                &:hover {
                    background-color: $color-gray-light;
                }
            }
        }
    }

    .waste-type-size-finder-loader {
        margin-top: 16px;
    }
</style>
